'use client';

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useChatStore } from '../../stores/chatStore';
import { ChatMessage } from '../../types/chat';
import { ChatBubble } from '../atoms/ChatBubble';

interface MessageListProps {
  messages: ChatMessage[];
  chatId: number;
}

export function MessageList({ messages, chatId }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInitialLoad = useRef(true);
  const lastMessageId = useRef<number | null>(null);
  const pendingScrollRestore = useRef<{ scrollTop: number, scrollHeight: number } | null>(null);
  const { loadMoreMessages, chatHistory, markAsRead } = useChatStore();
  const isLoadingMore = chatHistory[chatId]?.isLoadingMore || false;
  const hasMore = chatHistory[chatId]?.hasMore || false;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, []);

  const handleScroll = useCallback(async () => {
    if (!containerRef.current || isLoadingMore || !hasMore || isInitialLoad.current) return;

    const { scrollTop, scrollHeight } = containerRef.current;

    if (scrollTop <= 100) {
      // 무한 스크롤 전 스크롤 정보 저장
      pendingScrollRestore.current = {
        scrollTop,
        scrollHeight
      };

      await loadMoreMessages(chatId);
    }
  }, [chatId, isLoadingMore, hasMore, loadMoreMessages]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let timeoutId: NodeJS.Timeout;
    const debouncedHandler = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 150);
    };

    container.addEventListener('scroll', debouncedHandler);
    return () => {
      container.removeEventListener('scroll', debouncedHandler);
      clearTimeout(timeoutId);
    };
  }, [handleScroll]);

  useLayoutEffect(() => {
    // 메시지가 없으면 아무것도 하지 않음
    if (messages.length === 0) {
      lastMessageId.current = null;
      return;
    }

    const currentLastMessage = messages[messages.length - 1];
    const currentLastMessageId = currentLastMessage.id;

    // 첫 로딩인 경우 - 무조건 최하단으로 스크롤
    if (isInitialLoad.current) {
      scrollToBottom();
      isInitialLoad.current = false;
      lastMessageId.current = currentLastMessageId;
    }
    // 마지막 메시지 ID가 변경되었고, 로딩 중이 아닌 경우 (새 메시지 수신)
    else if (lastMessageId.current !== null &&
      lastMessageId.current !== currentLastMessageId &&
      !isLoadingMore) {
      scrollToBottom();
      lastMessageId.current = currentLastMessageId;
    }
    // 무한 스크롤로 메시지가 추가된 경우 (마지막 메시지 ID는 그대로)
    else if (lastMessageId.current === currentLastMessageId && !isInitialLoad.current) {
      // 무한 스크롤인 경우 - 스크롤 하지 않음
      // lastMessageId는 업데이트하지 않음
    }
    // lastMessageId 업데이트 (무한 스크롤이 아닌 경우)
    else if (lastMessageId.current === null) {
      lastMessageId.current = currentLastMessageId;
    }
  }, [messages, isLoadingMore, scrollToBottom]);

  // 무한 스크롤 후 스크롤 위치 즉시 복원 (DOM 업데이트 직후)
  useLayoutEffect(() => {
    if (pendingScrollRestore.current && containerRef.current) {
      const { scrollTop: previousScrollTop, scrollHeight: previousScrollHeight } = pendingScrollRestore.current;
      const currentScrollHeight = containerRef.current.scrollHeight;
      const scrollDiff = currentScrollHeight - previousScrollHeight;

      if (scrollDiff > 0) {
        // 새 메시지가 추가되어 스크롤 높이가 증가한 경우 위치 복원
        containerRef.current.scrollTop = previousScrollTop + scrollDiff;
      }

      pendingScrollRestore.current = null;
    }
  }, [messages]);

  // chatId가 변경되면 초기 로딩 상태로 리셋
  useEffect(() => {
    isInitialLoad.current = true;
    lastMessageId.current = null;
    pendingScrollRestore.current = null;
  }, [chatId]);

  // Mark unread messages as read when viewing chat
  useEffect(() => {
    if (messages.length > 0 && chatId) {
      const unreadMessageIds = messages
        .filter(msg => !msg.isOwn && !msg.isRead)
        .map(msg => msg.id);

      if (unreadMessageIds.length > 0) {
        const timeoutId = setTimeout(() => {
          markAsRead(chatId, unreadMessageIds);
        }, 500);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [messages, chatId, markAsRead]);

  const formatDateSeparator = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return '오늘';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return '어제';
    } else {
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
  };

  const shouldShowDateSeparator = (currentMessage: ChatMessage, previousMessage?: ChatMessage) => {
    if (!previousMessage) return true;

    const currentDate = new Date(currentMessage.timestamp);
    const previousDate = new Date(previousMessage.timestamp);

    return currentDate.toDateString() !== previousDate.toDateString();
  };

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto p-4 bg-[#FAFBFC]"
    >
      {messages.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <div className="w-16 h-16 bg-[#F5F7F9] rounded-full flex items-center justify-center mb-4">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 12H16M8 8H16M8 16H13M7 4H17C18.1046 4 19 4.89543 19 6V14C19 15.1046 18.1046 16 17 16H13L7 20V6C7 4.89543 7.89543 4 9 4H7Z"
                stroke="#ACB3BE"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="text-[#6D717D] text-sm">
            대화를 시작해보세요
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          {hasMore && (
            <div className="flex justify-center py-4">
              {isLoadingMore ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#6B7280]" />
              ) : (
                <div className="text-[#6D717D] text-xs">
                  위로 스크롤하여 이전 메시지 보기
                </div>
              )}
            </div>
          )}
          {messages.map((message, index) => {
            const previousMessage = index > 0 ? messages[index - 1] : undefined;
            const showDateSeparator = shouldShowDateSeparator(message, previousMessage);

            // 고유한 key 생성
            const uniqueKey = message.tempId || (message.id !== -1 ? `msg-${message.id}` : `temp-${index}-${message.timestamp?.getTime() || Date.now()}`);

            return (
              <div key={uniqueKey}>
                {/* Date separator */}
                {showDateSeparator && (
                  <div className="flex items-center justify-center my-4">
                    <div className="bg-[#F0F3F7] text-[#6D717D] text-xs px-3 py-1 rounded-full">
                      {formatDateSeparator(message.timestamp)}
                    </div>
                  </div>
                )}

                {/* Message bubble */}
                <ChatBubble message={message} />
              </div>
            );
          })}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>
      )}
    </div>
  );
}
